import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../store';
import { getApiUrl } from '../utils/api';
import { Settings, Globe, Clock, Info, Loader2, Save, Shield, FlaskConical, Trash2, AlertTriangle, User, FileText, Layers, Palette, RefreshCw, Mail } from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';
import { listItemCollections, type ItemCollection } from '../services/itemCollectionsApi';

interface SystemSettings {
  defaultTimezone: string;
  defaultSlotSize: number;
  calendarStartHour: number;
  calendarEndHour: number;
  calendarStepMinutes: number;
  appointmentMoveNotificationMode: 'IMMEDIATE' | 'MANUAL';
  assignAutoPractitionerPiercing: boolean;
  clientSelfCancelEnabled: boolean;
  otpValidityMinutes: number;
  otpMaxResendPerWindow: number;
  checkInCodeLength: number;
  checkInCodeAlphabet: string;
  isTestMode: boolean;
  operationMode: 'FULL' | 'NO_DELETE' | 'READ_ONLY';
  onlinePaymentProvider: 'STRIPE' | 'VIVA';
  tattooBookingEnabled: boolean;
  tattooBookingAuthPlacement: 'start' | 'before_upload' | 'before_summary';
  frontoffWorkingMode: 'full' | 'limited';
  piercingBookingZoneGuideEnabled: boolean;
  piercingBookingAnonymousEnabled: boolean;
  piercingBookingAttachConsentDocument: boolean;
  currentItemCollectionId: string;
  autoRefreshIntervalSeconds: number;
  systemVersion?: string;
  databaseType?: string;
}

export function GeneralParametersPage() {
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [purging, setPurging] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState(false);
  const [purgeResult, setPurgeResult] = useState<{ success: boolean; message: string } | null>(null);
  const [settings, setSettings] = useState<SystemSettings>({
    defaultTimezone: 'Europe/Paris',
    defaultSlotSize: 15,
    calendarStartHour: 7,
    calendarEndHour: 22,
    calendarStepMinutes: 15,
    appointmentMoveNotificationMode: 'IMMEDIATE',
    assignAutoPractitionerPiercing: true,
    clientSelfCancelEnabled: true,
    otpValidityMinutes: 10,
    otpMaxResendPerWindow: 3,
    checkInCodeLength: 4,
    checkInCodeAlphabet: 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789',
    isTestMode: false,
    operationMode: 'FULL',
    onlinePaymentProvider: 'STRIPE',
    tattooBookingEnabled: true,
    tattooBookingAuthPlacement: 'before_summary',
    frontoffWorkingMode: 'full',
    piercingBookingZoneGuideEnabled: false,
    piercingBookingAnonymousEnabled: false,
    piercingBookingAttachConsentDocument: true,
    currentItemCollectionId: '',
    autoRefreshIntervalSeconds: 30,
  });
  const [itemCollections, setItemCollections] = useState<ItemCollection[]>([]);

  useEffect(() => {
    loadSettings();
    listItemCollections({ limit: 1000, sortKey: 'name', sortDir: 'asc' })
      .then((data) => setItemCollections(data.itemCollections || []))
      .catch(() => setItemCollections([]));
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/settings/general'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          setSettings({
            defaultTimezone: data.data.defaultTimezone || 'Europe/Paris',
            defaultSlotSize: data.data.defaultSlotSize || 15,
            calendarStartHour: data.data.calendarStartHour ?? 7,
            calendarEndHour: data.data.calendarEndHour ?? 22,
            calendarStepMinutes: data.data.calendarStepMinutes ?? 15,
            appointmentMoveNotificationMode:
              data.data.appointmentMoveNotificationMode === 'MANUAL' ? 'MANUAL' : 'IMMEDIATE',
            assignAutoPractitionerPiercing: typeof data.data.assignAutoPractitionerPiercing === 'boolean'
              ? data.data.assignAutoPractitionerPiercing
              : true,
            clientSelfCancelEnabled: typeof data.data.clientSelfCancelEnabled === 'boolean'
              ? data.data.clientSelfCancelEnabled
              : true,
            otpValidityMinutes: data.data.otpValidityMinutes ?? 10,
            otpMaxResendPerWindow: data.data.otpMaxResendPerWindow ?? 3,
            checkInCodeLength: data.data.checkInCodeLength ?? 4,
            checkInCodeAlphabet: data.data.checkInCodeAlphabet || 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789',
            isTestMode: data.data.isTestMode ?? false,
            operationMode:
              data.data.operationMode === 'NO_DELETE' || data.data.operationMode === 'READ_ONLY'
                ? data.data.operationMode
                : 'FULL',
            onlinePaymentProvider: data.data.onlinePaymentProvider === 'VIVA' ? 'VIVA' : 'STRIPE',
            tattooBookingEnabled:
              typeof data.data.tattooBookingEnabled === 'boolean'
                ? data.data.tattooBookingEnabled
                : true,
            tattooBookingAuthPlacement:
              data.data.tattooBookingAuthPlacement === 'start' ||
              data.data.tattooBookingAuthPlacement === 'before_upload' ||
              data.data.tattooBookingAuthPlacement === 'before_summary'
                ? data.data.tattooBookingAuthPlacement
                : 'before_summary',
            frontoffWorkingMode: data.data.frontoffWorkingMode === 'limited' ? 'limited' : 'full',
            piercingBookingZoneGuideEnabled:
              typeof data.data.piercingBookingZoneGuideEnabled === 'boolean'
                ? data.data.piercingBookingZoneGuideEnabled
                : false,
            piercingBookingAnonymousEnabled:
              typeof data.data.piercingBookingAnonymousEnabled === 'boolean'
                ? data.data.piercingBookingAnonymousEnabled
                : false,
            piercingBookingAttachConsentDocument:
              typeof data.data.piercingBookingAttachConsentDocument === 'boolean'
                ? data.data.piercingBookingAttachConsentDocument
                : true,
            currentItemCollectionId: data.data.currentItemCollectionId || '',
            autoRefreshIntervalSeconds: data.data.autoRefreshIntervalSeconds ?? 30,
            systemVersion: data.data.systemVersion,
            databaseType: data.data.databaseType,
          });
        }
      } else {
        // If 403, user doesn't have permission (not Super Admin)
        if (response.status === 403) {
          const errorData = await response.json();
          setError(errorData.message || t('settings.general.errors.loadFailed'));
        } else {
          // Fallback to defaults if endpoint doesn't exist or other error
          setSettings({
            defaultTimezone: 'Europe/Paris',
            defaultSlotSize: 15,
            calendarStartHour: 7,
            calendarEndHour: 22,
            calendarStepMinutes: 15,
            appointmentMoveNotificationMode: 'IMMEDIATE',
            assignAutoPractitionerPiercing: true,
            clientSelfCancelEnabled: true,
            otpValidityMinutes: 10,
            otpMaxResendPerWindow: 3,
            checkInCodeLength: 4,
            checkInCodeAlphabet: 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789',
            isTestMode: false,
            operationMode: 'FULL',
            onlinePaymentProvider: 'STRIPE',
            tattooBookingEnabled: true,
            tattooBookingAuthPlacement: 'before_summary',
            frontoffWorkingMode: 'full',
            piercingBookingZoneGuideEnabled: false,
            piercingBookingAnonymousEnabled: false,
            piercingBookingAttachConsentDocument: true,
            currentItemCollectionId: '',
            autoRefreshIntervalSeconds: 30,
            systemVersion: '1.0.0',
            databaseType: 'PostgreSQL'
          });
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      // Fallback to defaults on error
      setSettings({
        defaultTimezone: 'Europe/Paris',
        defaultSlotSize: 15,
        calendarStartHour: 7,
        calendarEndHour: 22,
        calendarStepMinutes: 15,
        appointmentMoveNotificationMode: 'IMMEDIATE',
        assignAutoPractitionerPiercing: true,
        clientSelfCancelEnabled: true,
        otpValidityMinutes: 10,
        otpMaxResendPerWindow: 3,
        checkInCodeLength: 4,
        checkInCodeAlphabet: 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789',
        isTestMode: false,
        operationMode: 'FULL',
        onlinePaymentProvider: 'STRIPE',
        tattooBookingEnabled: true,
        tattooBookingAuthPlacement: 'before_summary',
        frontoffWorkingMode: 'full',
        piercingBookingZoneGuideEnabled: false,
        piercingBookingAnonymousEnabled: false,
        piercingBookingAttachConsentDocument: true,
        currentItemCollectionId: '',
        autoRefreshIntervalSeconds: 30,
        systemVersion: '1.0.0',
        databaseType: 'PostgreSQL'
      });
      // Only show error if it's not a network error (endpoint might not exist yet)
      if (error instanceof TypeError && error.message.includes('fetch')) {
        // Network error, use defaults silently
      } else {
        setError(t('settings.general.errors.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    field: 'defaultTimezone' | 'defaultSlotSize' | 'calendarStartHour' | 'calendarEndHour' | 'calendarStepMinutes' | 'otpValidityMinutes' | 'otpMaxResendPerWindow' | 'checkInCodeLength' | 'checkInCodeAlphabet' | 'autoRefreshIntervalSeconds',
    value: string | number
  ) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear messages when user starts editing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleToggleAutoAssignPiercing = () => {
    setSettings(prev => ({
      ...prev,
      assignAutoPractitionerPiercing: !prev.assignAutoPractitionerPiercing
    }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleTogglePiercingBookingZoneGuide = () => {
    setSettings(prev => ({
      ...prev,
      piercingBookingZoneGuideEnabled: !prev.piercingBookingZoneGuideEnabled
    }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleTogglePiercingBookingAnonymous = () => {
    setSettings(prev => ({
      ...prev,
      piercingBookingAnonymousEnabled: !prev.piercingBookingAnonymousEnabled
    }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleTogglePiercingBookingAttachConsentDocument = () => {
    setSettings(prev => ({
      ...prev,
      piercingBookingAttachConsentDocument: !prev.piercingBookingAttachConsentDocument
    }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleToggleClientSelfCancel = () => {
    setSettings(prev => ({
      ...prev,
      clientSelfCancelEnabled: !prev.clientSelfCancelEnabled
    }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handlePurgeTestData = async () => {
    if (!purgeConfirm) {
      setPurgeConfirm(true);
      return;
    }
    try {
      setPurging(true);
      setPurgeConfirm(false);
      setPurgeResult(null);
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/admin/settings/purge-test-data'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ purgeAllClients: false, purgeAppointments: true, purgeLogs: true, purgeTestCases: true }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        const { deletedCounts, filesDeleted, durationMs } = data.data;
        const totalRows = Object.values(deletedCounts as Record<string, number>).reduce((a, b) => a + b, 0);
        setPurgeResult({
          success: true,
          message: `Purge terminée en ${(durationMs / 1000).toFixed(1)}s — ${totalRows} enregistrements supprimés, ${filesDeleted} fichiers supprimés.`,
        });
      } else {
        setPurgeResult({ success: false, message: data.message || 'Échec de la purge.' });
      }
    } catch (err) {
      setPurgeResult({ success: false, message: 'Erreur réseau lors de la purge.' });
    } finally {
      setPurging(false);
    }
  };

  const validateForm = (): boolean => {
    if (!settings.defaultTimezone || settings.defaultTimezone.trim() === '') {
      setError(t('settings.general.validation.timezoneRequired'));
      return false;
    }
    
    if (!settings.defaultSlotSize || settings.defaultSlotSize < 5 || settings.defaultSlotSize > 120) {
      setError(t('settings.general.validation.slotSizeInvalid'));
      return false;
    }

    if (settings.calendarEndHour <= settings.calendarStartHour) {
      setError(t('settings.general.validation.calendarHoursInvalid'));
      return false;
    }

    if (![15, 30, 60].includes(settings.calendarStepMinutes)) {
      setError(t('settings.general.validation.calendarStepInvalid'));
      return false;
    }

    if (settings.otpValidityMinutes < 1 || settings.otpValidityMinutes > 60) {
      setError(t('settings.general.validation.otpValidityInvalid'));
      return false;
    }

    if (settings.otpMaxResendPerWindow < 1 || settings.otpMaxResendPerWindow > 10) {
      setError(t('settings.general.validation.otpMaxResendInvalid'));
      return false;
    }

    if (settings.checkInCodeLength < 3 || settings.checkInCodeLength > 8) {
      setError(t('settings.general.validation.checkInCodeLengthInvalid'));
      return false;
    }

    const alphabet = settings.checkInCodeAlphabet.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const uniqueAlphabet = [...new Set(alphabet.split(''))].join('');
    if (uniqueAlphabet.length < 10) {
      setError(t('settings.general.validation.checkInCodeAlphabetInvalid'));
      return false;
    }

    if (
      settings.autoRefreshIntervalSeconds !== 0 &&
      (settings.autoRefreshIntervalSeconds < 5 || settings.autoRefreshIntervalSeconds > 3600)
    ) {
      setError('L\'intervalle de rafraîchissement automatique doit être 0 (désactivé) ou compris entre 5 et 3600 secondes.');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('token');
      
      // TODO: Replace with actual API call when backend endpoint is ready
      // For now, we'll prepare the call structure
      const response = await fetch(getApiUrl('/api/admin/settings/general'), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          defaultTimezone: settings.defaultTimezone,
          defaultSlotSize: settings.defaultSlotSize,
          calendarStartHour: settings.calendarStartHour,
          calendarEndHour: settings.calendarEndHour,
          calendarStepMinutes: settings.calendarStepMinutes,
          appointmentMoveNotificationMode: settings.appointmentMoveNotificationMode,
          assignAutoPractitionerPiercing: settings.assignAutoPractitionerPiercing,
          clientSelfCancelEnabled: settings.clientSelfCancelEnabled,
          otpValidityMinutes: settings.otpValidityMinutes,
          otpMaxResendPerWindow: settings.otpMaxResendPerWindow,
          checkInCodeLength: settings.checkInCodeLength,
          checkInCodeAlphabet: [...new Set(settings.checkInCodeAlphabet.toUpperCase().replace(/[^A-Z0-9]/g, '').split(''))].join(''),
          isTestMode: settings.isTestMode,
          operationMode: settings.operationMode,
          onlinePaymentProvider: settings.onlinePaymentProvider,
          tattooBookingEnabled: settings.tattooBookingEnabled,
          tattooBookingAuthPlacement: settings.tattooBookingAuthPlacement,
          frontoffWorkingMode: settings.frontoffWorkingMode,
          piercingBookingZoneGuideEnabled: settings.piercingBookingZoneGuideEnabled,
          piercingBookingAnonymousEnabled: settings.piercingBookingAnonymousEnabled,
          piercingBookingAttachConsentDocument: settings.piercingBookingAttachConsentDocument,
          currentItemCollectionId: settings.currentItemCollectionId || undefined,
          autoRefreshIntervalSeconds: settings.autoRefreshIntervalSeconds,
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(data.message || t('settings.general.success.saved'));
        window.dispatchEvent(
          new CustomEvent('app:operation-mode-changed', {
            detail: { mode: data.data?.operationMode || settings.operationMode },
          }),
        );
        // Reload settings to get updated values
        await loadSettings();
      } else {
        const errorData = await response.json();
        setError(errorData.message || t('settings.general.errors.saveFailed'));
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setError(t('settings.general.errors.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Common timezones
  const commonTimezones = [
    'Europe/Paris',
    'Europe/London',
    'Europe/Berlin',
    'Europe/Madrid',
    'Europe/Rome',
    'Europe/Amsterdam',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Australia/Sydney',
    'UTC'
  ];

  // Common slot sizes
  const slotSizes = [5, 10, 15, 20, 30, 45, 60, 90, 120];
  const calendarHours = Array.from({ length: 24 }, (_, i) => i);
  const calendarStepOptions = [15, 30, 60];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Check if user has permission to edit (Super Admin only for now)
  const canEdit = user?.role === 'super_admin';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-textPrimary flex items-center space-x-3">
          <Settings className="w-8 h-8 text-primary-600" />
          <span>{t('settings.general.title')}</span>
          <HelpIcon 
            docPath="16-parametres#16-2-paramètres-généraux" 
            tooltip="Documentation : Paramètres généraux"
          />
        </h1>
        <p className="text-textSecondary mt-2">
          {t('settings.general.subtitle')}
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Settings Form */}
      <form onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Settings Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Default Timezone */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <Globe className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('settings.general.timezone.title')}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('settings.general.timezone.description')}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4">
            {canEdit ? (
              <select
                value={settings.defaultTimezone}
                onChange={(e) => handleInputChange('defaultTimezone', e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                {commonTimezones.map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            ) : (
              <>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {settings.defaultTimezone}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('settings.general.readOnly')}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <Clock className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('settings.general.calendar.title')}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('settings.general.calendar.description')}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.general.calendar.startHour')}
              </label>
              {canEdit ? (
                <select
                  value={settings.calendarStartHour}
                  onChange={(e) => handleInputChange('calendarStartHour', parseInt(e.target.value, 10))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white"
                >
                  {calendarHours.map(hour => (
                    <option key={hour} value={hour}>{`${hour.toString().padStart(2, '0')}:00`}</option>
                  ))}
                </select>
              ) : (
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {`${settings.calendarStartHour.toString().padStart(2, '0')}:00`}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.general.calendar.endHour')}
              </label>
              {canEdit ? (
                <select
                  value={settings.calendarEndHour}
                  onChange={(e) => handleInputChange('calendarEndHour', parseInt(e.target.value, 10))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white"
                >
                  {calendarHours.map(hour => (
                    <option key={hour} value={hour}>{`${hour.toString().padStart(2, '0')}:00`}</option>
                  ))}
                </select>
              ) : (
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {`${settings.calendarEndHour.toString().padStart(2, '0')}:00`}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.general.calendar.stepMinutes')}
              </label>
              {canEdit ? (
                <select
                  value={settings.calendarStepMinutes}
                  onChange={(e) => handleInputChange('calendarStepMinutes', parseInt(e.target.value, 10))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white"
                >
                  {calendarStepOptions.map(step => (
                    <option key={step} value={step}>{step} {t('settings.general.slotSize.minutes')}</option>
                  ))}
                </select>
              ) : (
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {settings.calendarStepMinutes} {t('settings.general.slotSize.minutes')}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* APPOINTMENT_MOVE_NOTIFICATION_MODE */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <Mail className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Notification après déplacement d’un rendez-vous
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Définit quand l’e-mail informant le client du nouvel horaire est envoyé.
                </p>
              </div>
            </div>
          </div>

          {canEdit ? (
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="appointmentMoveNotificationMode"
                  value="IMMEDIATE"
                  checked={settings.appointmentMoveNotificationMode === 'IMMEDIATE'}
                  onChange={() => setSettings((prev) => ({ ...prev, appointmentMoveNotificationMode: 'IMMEDIATE' }))}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-700">Envoi immédiat</span>
                  <span className="block text-xs text-gray-500">Chaque déplacement envoie automatiquement un e-mail.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="appointmentMoveNotificationMode"
                  value="MANUAL"
                  checked={settings.appointmentMoveNotificationMode === 'MANUAL'}
                  onChange={() => setSettings((prev) => ({ ...prev, appointmentMoveNotificationMode: 'MANUAL' }))}
                  className="mt-1"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-700">Envoi manuel après validation</span>
                  <span className="block text-xs text-gray-500">
                    Les déplacements sont enregistrés sans e-mail. Une action sur le rendez-vous permet d’envoyer le dernier horaire.
                  </span>
                </span>
              </label>
            </div>
          ) : (
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
              {settings.appointmentMoveNotificationMode === 'MANUAL'
                ? 'Envoi manuel après validation'
                : 'Envoi immédiat'}
            </div>
          )}
        </div>

        {/* AUTO_REFRESH_INTERVAL_SECONDS */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <RefreshCw className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Rafraîchissement automatique des tableaux de bord
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Intervalle utilisé par les tableaux de bord backoffice pour se rafraîchir automatiquement (0 = désactivé).
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-xs">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Intervalle (secondes)
            </label>
            {canEdit ? (
              <input
                type="number"
                min={0}
                max={3600}
                step={5}
                value={settings.autoRefreshIntervalSeconds}
                onChange={(e) => handleInputChange('autoRefreshIntervalSeconds', parseInt(e.target.value, 10) || 0)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white"
              />
            ) : (
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                {settings.autoRefreshIntervalSeconds}s
              </div>
            )}
            <p className="text-xs text-gray-400 mt-2">
              0 désactive le rafraîchissement automatique ; sinon entre 5 et 3600 secondes.
            </p>
          </div>
        </div>

        {/* ASSIGN_AUTO_PRACTITIONER_PIERCING */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <Clock className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  ASSIGN_AUTO_PRACTITIONER_PIERCING
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {settings.assignAutoPractitionerPiercing
                    ? 'Assigne automatiquement un praticien (piercing) si disponible'
                    : 'Crée le rendez-vous piercing avec la boutique uniquement (sans praticien)'}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            {canEdit ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {settings.assignAutoPractitionerPiercing ? 'Activé' : 'Désactivé'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {settings.assignAutoPractitionerPiercing
                      ? 'Le système choisit un praticien libre (et compatible piercing) au moment de la réservation.'
                      : 'Le client réserve uniquement un créneau dans une boutique; l’assignation se fait plus tard.'}
                  </p>
                </div>
                <button
                  onClick={handleToggleAutoAssignPiercing}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    settings.assignAutoPractitionerPiercing ? 'bg-primary-600' : 'bg-gray-200'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  role="switch"
                  aria-checked={settings.assignAutoPractitionerPiercing}
                  aria-label="Toggle ASSIGN_AUTO_PRACTITIONER_PIERCING"
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      settings.assignAutoPractitionerPiercing ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ) : (
              <>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {settings.assignAutoPractitionerPiercing ? 'Activé' : 'Désactivé'}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('settings.general.readOnly')}
                </p>
              </>
            )}
          </div>
        </div>

        {/* CURRENT_ITEM_COLLECTION_ID */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <Layers className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('settings.general.currentItemCollection.title')}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('settings.general.currentItemCollection.description')}
                </p>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.general.currentItemCollection.label')}
            </label>
            {canEdit ? (
              <select
                value={settings.currentItemCollectionId}
                onChange={(e) => {
                  setSettings((prev) => ({ ...prev, currentItemCollectionId: e.target.value }));
                  if (error) setError('');
                  if (success) setSuccess('');
                }}
                className="input-field max-w-lg"
                disabled={saving}
              >
                <option value="">{t('catalog.itemCollections.selectCollection')}</option>
                {itemCollections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.code})
                  </option>
                ))}
              </select>
            ) : (
              <>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {itemCollections.find((c) => c.id === settings.currentItemCollectionId)?.name ||
                    settings.currentItemCollectionId ||
                    '—'}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('settings.general.readOnly')}
                </p>
              </>
            )}
          </div>
        </div>

        {/* PIERCING_BOOKING_ZONE_GUIDE_ENABLED */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <Settings className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  PIERCING_BOOKING_ZONE_GUIDE_ENABLED
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {settings.piercingBookingZoneGuideEnabled
                    ? 'Affiche les images de localisation des piercings pendant la réservation'
                    : 'Masque les images de localisation des piercings (code conservé)'}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            {canEdit ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {settings.piercingBookingZoneGuideEnabled ? 'Activé' : 'Désactivé'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {settings.piercingBookingZoneGuideEnabled
                      ? 'Le client voit le guide anatomique (zone / hotspots) à l’étape de choix du piercing.'
                      : 'Le parcours piercing reste en liste déroulante uniquement, sans visualisation.'}
                  </p>
                </div>
                <button
                  onClick={handleTogglePiercingBookingZoneGuide}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    settings.piercingBookingZoneGuideEnabled ? 'bg-primary-600' : 'bg-gray-200'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  role="switch"
                  aria-checked={settings.piercingBookingZoneGuideEnabled}
                  aria-label="Toggle PIERCING_BOOKING_ZONE_GUIDE_ENABLED"
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      settings.piercingBookingZoneGuideEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ) : (
              <>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {settings.piercingBookingZoneGuideEnabled ? 'Activé' : 'Désactivé'}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('settings.general.readOnly')}
                </p>
              </>
            )}
          </div>
        </div>

        {/* PIERCING_BOOKING_ANONYMOUS_ENABLED */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <User className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  PIERCING_BOOKING_ANONYMOUS_ENABLED
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Autorise ou masque la réservation anonyme dans le parcours piercing du frontoffice.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            {canEdit ? (
              <div className="flex items-center justify-between gap-6">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {settings.piercingBookingAnonymousEnabled ? 'Activé' : 'Désactivé'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {settings.piercingBookingAnonymousEnabled
                      ? 'Le visiteur peut continuer sans compte, sans e-mail et sans téléphone.'
                      : 'Le visiteur doit fournir un contact, se connecter ou créer un compte. La création anonyme reste disponible dans le backoffice.'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleTogglePiercingBookingAnonymous}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    settings.piercingBookingAnonymousEnabled ? 'bg-primary-600' : 'bg-gray-200'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  role="switch"
                  aria-checked={settings.piercingBookingAnonymousEnabled}
                  aria-label="Toggle PIERCING_BOOKING_ANONYMOUS_ENABLED"
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      settings.piercingBookingAnonymousEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ) : (
              <>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {settings.piercingBookingAnonymousEnabled ? 'Activé' : 'Désactivé'}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('settings.general.readOnly')}
                </p>
              </>
            )}
          </div>
        </div>

        {/* PIERCING_BOOKING_ATTACH_CONSENT_DOCUMENT */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <FileText className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  PIERCING_BOOKING_ATTACH_CONSENT_DOCUMENT
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {settings.piercingBookingAttachConsentDocument
                    ? 'Joindre le contrat de consentement piercing à l’e-mail de confirmation'
                    : 'Envoyer l’e-mail de confirmation sans document joint'}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            {canEdit ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {settings.piercingBookingAttachConsentDocument ? 'Activé' : 'Désactivé'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {settings.piercingBookingAttachConsentDocument
                      ? 'À la fin de la réservation piercing, le PDF de consentement est attaché à l’e-mail client.'
                      : 'L’e-mail de confirmation est envoyé, mais sans le contrat de consentement.'}
                  </p>
                </div>
                <button
                  onClick={handleTogglePiercingBookingAttachConsentDocument}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    settings.piercingBookingAttachConsentDocument ? 'bg-primary-600' : 'bg-gray-200'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  role="switch"
                  aria-checked={settings.piercingBookingAttachConsentDocument}
                  aria-label="Toggle PIERCING_BOOKING_ATTACH_CONSENT_DOCUMENT"
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      settings.piercingBookingAttachConsentDocument ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ) : (
              <>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {settings.piercingBookingAttachConsentDocument ? 'Activé' : 'Désactivé'}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('settings.general.readOnly')}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Client self-cancel */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <Shield className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Annulation en ligne par le client
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Active le lien d&apos;annulation dans les e-mails de confirmation.
                  Le délai (défaut 24 h avant le RDV) se règle dans les règles de planning du shop.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-6">
            {canEdit ? (
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    {settings.clientSelfCancelEnabled ? 'Activé' : 'Désactivé'}
                  </p>
                </div>
                <button
                  onClick={handleToggleClientSelfCancel}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                    settings.clientSelfCancelEnabled ? 'bg-primary-600' : 'bg-gray-200'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  role="switch"
                  aria-checked={settings.clientSelfCancelEnabled}
                  aria-label="Toggle client self cancel"
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      settings.clientSelfCancelEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ) : (
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                {settings.clientSelfCancelEnabled ? 'Activé' : 'Désactivé'}
              </div>
            )}
          </div>
        </div>

        {/* OTP (piercing booking verification) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <Shield className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('settings.general.otp.title')}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('settings.general.otp.description')}
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.general.otp.validityMinutes')}
              </label>
              {canEdit ? (
                <select
                  value={settings.otpValidityMinutes}
                  onChange={(e) => handleInputChange('otpValidityMinutes', parseInt(e.target.value, 10))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white"
                >
                  {[5, 10, 15, 20, 30, 45, 60].map(m => (
                    <option key={m} value={m}>{m} {t('settings.general.slotSize.minutes')}</option>
                  ))}
                </select>
              ) : (
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {settings.otpValidityMinutes} {t('settings.general.slotSize.minutes')}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.general.otp.maxResendPerWindow')}
              </label>
              {canEdit ? (
                <select
                  value={settings.otpMaxResendPerWindow}
                  onChange={(e) => handleInputChange('otpMaxResendPerWindow', parseInt(e.target.value, 10))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              ) : (
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {settings.otpMaxResendPerWindow}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Check-in code (oral reception code) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <Shield className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('settings.general.checkInCode.title')}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('settings.general.checkInCode.description')}
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.general.checkInCode.length')}
              </label>
              {canEdit ? (
                <select
                  value={settings.checkInCodeLength}
                  onChange={(e) => handleInputChange('checkInCodeLength', parseInt(e.target.value, 10))}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white"
                >
                  {[3, 4, 5, 6, 7, 8].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              ) : (
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {settings.checkInCodeLength}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.general.checkInCode.alphabet')}
              </label>
              {canEdit ? (
                <input
                  type="text"
                  value={settings.checkInCodeAlphabet}
                  onChange={(e) =>
                    handleInputChange(
                      'checkInCodeAlphabet',
                      e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''),
                    )
                  }
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white font-mono text-sm tracking-wider"
                  spellCheck={false}
                  autoCorrect="off"
                />
              ) : (
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 font-mono text-sm break-all">
                  {settings.checkInCodeAlphabet}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                {t('settings.general.checkInCode.alphabetHint')}
              </p>
            </div>
          </div>
        </div>

        {/* Default Slot Size */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-primary-100 rounded-lg">
                <Clock className="w-5 h-5 text-primary-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('settings.general.slotSize.title')}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('settings.general.slotSize.description')}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4">
            {canEdit ? (
              <select
                value={settings.defaultSlotSize}
                onChange={(e) => handleInputChange('defaultSlotSize', parseInt(e.target.value))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white"
              >
                {slotSizes.map(size => (
                  <option key={size} value={size}>{size} {t('settings.general.slotSize.minutes')}</option>
                ))}
              </select>
            ) : (
              <>
                <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {settings.defaultSlotSize} {t('settings.general.slotSize.minutes')}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {t('settings.general.readOnly')}
                </p>
              </>
            )}
          </div>
        </div>
        </div>

        {/* Online Payment Provider */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 bg-green-100 rounded-lg">
              <Shield className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Prestataire de paiement en ligne</h2>
              <p className="text-sm text-gray-500 mt-1">
                Choisir la solution active pour les acomptes en ligne. Stripe et Viva.com restent disponibles dans le code.
              </p>
            </div>
          </div>

          {canEdit ? (
            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="onlinePaymentProvider"
                  value="STRIPE"
                  checked={settings.onlinePaymentProvider === 'STRIPE'}
                  onChange={() => setSettings((prev) => ({ ...prev, onlinePaymentProvider: 'STRIPE' }))}
                />
                <span className="text-sm font-medium text-gray-800">Stripe</span>
              </label>
              <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="onlinePaymentProvider"
                  value="VIVA"
                  checked={settings.onlinePaymentProvider === 'VIVA'}
                  onChange={() => setSettings((prev) => ({ ...prev, onlinePaymentProvider: 'VIVA' }))}
                />
                <span className="text-sm font-medium text-gray-800">Viva.com</span>
              </label>
            </div>
          ) : (
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
              {settings.onlinePaymentProvider === 'VIVA' ? 'Viva.com' : 'Stripe'}
            </div>
          )}
        </div>

        {/* Frontoffice working mode */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 bg-violet-100 rounded-lg">
              <Globe className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Mode du front-office</h2>
              <p className="text-sm text-gray-500 mt-1">
                Le mode limité conserve l&apos;accueil, l&apos;authentification et les réservations, en français uniquement.
              </p>
            </div>
          </div>

          {canEdit ? (
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="frontoffWorkingMode"
                  value="full"
                  className="mt-1"
                  checked={settings.frontoffWorkingMode === 'full'}
                  onChange={() => setSettings((prev) => ({ ...prev, frontoffWorkingMode: 'full' }))}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-800">Complet</span>
                  <span className="block text-xs text-gray-500">Comportement actuel avec toutes les fonctionnalités.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="frontoffWorkingMode"
                  value="limited"
                  className="mt-1"
                  checked={settings.frontoffWorkingMode === 'limited'}
                  onChange={() => setSettings((prev) => ({ ...prev, frontoffWorkingMode: 'limited' }))}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-800">Limité</span>
                  <span className="block text-xs text-gray-500">Masque le guide, le choix de langue, la boutique et les appels à l&apos;action secondaires.</span>
                </span>
              </label>
            </div>
          ) : (
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
              {settings.frontoffWorkingMode === 'limited' ? 'Limité' : 'Complet'}
            </div>
          )}
        </div>

        {/* Tattoo booking auth placement */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 bg-indigo-100 rounded-lg">
              <User className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Réservation tattoo — connexion</h2>
              <p className="text-sm text-gray-500 mt-1">
                Où demander la connexion ou la création de compte dans le parcours front-office.
              </p>
            </div>
          </div>

          {canEdit ? (
            <div className="space-y-3">
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="tattooBookingAuthPlacement"
                  value="start"
                  className="mt-1"
                  checked={settings.tattooBookingAuthPlacement === 'start'}
                  onChange={() => setSettings((prev) => ({ ...prev, tattooBookingAuthPlacement: 'start' }))}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-800">Au début</span>
                  <span className="block text-xs text-gray-500">Comportement historique — identité avant le choix du studio.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="tattooBookingAuthPlacement"
                  value="before_upload"
                  className="mt-1"
                  checked={settings.tattooBookingAuthPlacement === 'before_upload'}
                  onChange={() => setSettings((prev) => ({ ...prev, tattooBookingAuthPlacement: 'before_upload' }))}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-800">Avant le projet / images</span>
                  <span className="block text-xs text-gray-500">Le client parcourt studio, service et artiste avant de s&apos;identifier.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                <input
                  type="radio"
                  name="tattooBookingAuthPlacement"
                  value="before_summary"
                  className="mt-1"
                  checked={settings.tattooBookingAuthPlacement === 'before_summary'}
                  onChange={() => setSettings((prev) => ({ ...prev, tattooBookingAuthPlacement: 'before_summary' }))}
                />
                <span>
                  <span className="block text-sm font-medium text-gray-800">Avant le récapitulatif (recommandé)</span>
                  <span className="block text-xs text-gray-500">Identité juste avant la confirmation — les images locales sont envoyées après connexion.</span>
                </span>
              </label>
            </div>
          ) : (
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
              {settings.tattooBookingAuthPlacement === 'start'
                ? 'Au début'
                : settings.tattooBookingAuthPlacement === 'before_upload'
                  ? 'Avant le projet / images'
                  : 'Avant le récapitulatif'}
            </div>
          )}
        </div>

        {/* Tattoo booking enabled */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 bg-rose-100 rounded-lg">
              <Palette className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Réservation tattoo</h2>
              <p className="text-sm text-gray-500 mt-1">
                Active ou masque la réservation de tatouages dans le front-office.
              </p>
            </div>
          </div>

          {canEdit ? (
            <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <input
                type="checkbox"
                checked={settings.tattooBookingEnabled}
                onChange={(e) => {
                  setSettings((prev) => ({ ...prev, tattooBookingEnabled: e.target.checked }));
                  if (error) setError('');
                  if (success) setSuccess('');
                }}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                disabled={saving}
              />
              <span>
                <span className="block text-sm font-medium text-gray-800">
                  {settings.tattooBookingEnabled ? 'Réservation tattoo activée' : 'Réservation tattoo désactivée'}
                </span>
                <span className="block text-xs text-gray-500">
                  Si désactivée, les boutons tattoo sont masqués et l&apos;URL /book-tattoo redirige vers l&apos;accueil.
                </span>
              </span>
            </label>
          ) : (
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
              {settings.tattooBookingEnabled ? 'Activée' : 'Désactivée'}
            </div>
          )}
        </div>

        {/* Application operation mode */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-start space-x-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Mode de fonctionnement</h2>
              <p className="text-sm text-gray-500 mt-1">
                Définit la politique globale d’écriture et de suppression de l’application.
              </p>
            </div>
          </div>

          {canEdit ? (
            <select
              value={settings.operationMode}
              onChange={(e) =>
                setSettings((prev) => ({
                  ...prev,
                  operationMode: e.target.value as SystemSettings['operationMode'],
                }))
              }
              disabled={saving}
              className="input-field"
            >
              <option value="FULL">FULL — fonctionnement normal</option>
              <option value="NO_DELETE">
                NO_DELETE — suppressions interdites, sauf Super Admin
              </option>
              <option value="READ_ONLY">
                READ_ONLY — aucune modification, sauf sortie du mode par un Super Admin
              </option>
            </select>
          ) : (
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
              {settings.operationMode}
            </div>
          )}

          <p className="text-xs text-gray-500 mt-3">
            Les annulations de rendez-vous et les désactivations restent autorisées en NO_DELETE.
            Les Managers ne peuvent jamais supprimer, quel que soit ce réglage.
          </p>
        </div>

        {/* Test Mode */}
        <div className="bg-white rounded-lg shadow-sm border border-amber-200 p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-amber-100 rounded-lg">
                <FlaskConical className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Mode Test</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Active la gestion des cas de test dans l'interface d'administration. Ne jamais activer en production.
                </p>
              </div>
            </div>
          </div>

          {canEdit ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm font-medium ${settings.isTestMode ? 'text-amber-700' : 'text-gray-700'}`}>
                    {settings.isTestMode ? '⚠️ Mode TEST activé' : 'Désactivé'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {settings.isTestMode
                      ? 'Le menu "Tests" est visible dans le backoffice. Une bannière orange est affichée.'
                      : 'Activer pour afficher le gestionnaire de cas de test dans le menu Admin.'}
                  </p>
                </div>
                <button
                  onClick={() => setSettings((prev) => ({ ...prev, isTestMode: !prev.isTestMode }))}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${
                    settings.isTestMode ? 'bg-amber-500' : 'bg-gray-200'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  role="switch"
                  aria-checked={settings.isTestMode}
                  aria-label="Toggle test mode"
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      settings.isTestMode ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Purge test data — only shown when test mode is active */}
              {settings.isTestMode && (
                <div className="border-t border-amber-200 pt-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-700 flex items-center gap-1">
                        <AlertTriangle className="w-4 h-4" />
                        Purger les données de test
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Supprime toutes les données transactionnelles (rendez-vous, actes, clients de test, documents générés, logs, cas de test, fichiers). Les données structurelles (shops, praticiens, services…) sont préservées.
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {purgeConfirm ? (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-red-600 font-medium">Confirmer ?</span>
                          <button
                            onClick={handlePurgeTestData}
                            disabled={purging}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                          >
                            {purging ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                            Oui, purger
                          </button>
                          <button
                            onClick={() => setPurgeConfirm(false)}
                            disabled={purging}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                          >
                            Annuler
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={handlePurgeTestData}
                          disabled={purging}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 hover:bg-red-100 rounded-md disabled:opacity-50"
                        >
                          {purging ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                          {purging ? 'Purge en cours…' : 'Purger les données'}
                        </button>
                      )}
                    </div>
                  </div>

                  {purgeResult && (
                    <div className={`mt-3 p-3 rounded-md text-xs ${purgeResult.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                      {purgeResult.message}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700 text-sm">
              {settings.isTestMode ? 'Activé' : 'Désactivé'}
            </div>
          )}
        </div>

        {/* System Information */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg">
            <Info className="w-5 h-5 text-gray-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('settings.general.systemInfo.title')}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {t('settings.general.systemInfo.description')}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {settings.systemVersion && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.general.systemInfo.version')}
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                {settings.systemVersion}
              </div>
            </div>
          )}
          {settings.databaseType && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.general.systemInfo.database')}
              </label>
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                {settings.databaseType}
              </div>
            </div>
          )}
        </div>
        </div>

        {/* Save Button - Only for Super Admins */}
        {canEdit && (
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('settings.general.saving')}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {t('settings.general.save')}
                </>
              )}
            </button>
          </div>
        )}
      </form>

      {/* Info Section */}
      <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">
          {t('settings.general.note.title')}
        </h3>
        <p className="text-sm text-blue-800">
          {t('settings.general.note.description')}
        </p>
      </div>
    </div>
  );
}

